import { useRef, useState, useEffect } from 'react';
import { GitBranch, Server, MessageSquare, CheckCircle, Code2 } from 'lucide-react';
import { useTr } from '../contexts/LanguageContext';

const useInView = (threshold = 0.08) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const PillarCard = ({ pillar, index }) => {
  const { ref, inView } = useInView(0.06);
  const Icon = pillar.icon;
  return (
    <div ref={ref} data-testid={`pillar-${pillar.id}`}
      className={`glass card-3d animated-border rounded-2xl p-6 sm:p-7 flex flex-col gap-4 sm:gap-5 anim-hidden ${inView ? 'anim-visible' : ''}`}
      style={{ transitionDelay: `${index * 0.09}s` }}>
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center relative" style={{ background: 'rgba(48,213,200,0.1)', border: '1px solid rgba(48,213,200,0.25)' }}>
          <Icon size={18} color="#30d5c8" />
          <div className="absolute inset-0 rounded-xl" style={{ background: 'radial-gradient(circle at center, rgba(48,213,200,0.15) 0%, transparent 70%)', animation: `radial-pulse ${3 + index * 0.5}s ease-in-out infinite` }} />
        </div>
        <span className="font-mono text-5xl font-black" style={{ color: 'rgba(48,213,200,0.06)' }}>{pillar.number}</span>
      </div>
      <div>
        <p className="text-[10px] sm:text-xs font-mono text-[#30d5c8] tracking-widest uppercase mb-1">{pillar.subtitle}</p>
        <h3 className="font-outfit font-black text-white text-lg sm:text-xl md:text-2xl">{pillar.name}</h3>
      </div>
      <p className="text-white/50 text-xs sm:text-sm leading-relaxed">{pillar.desc}</p>
      <ul className="mt-auto flex flex-col gap-1.5 sm:gap-2">
        {pillar.features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-xs text-white/65">
            <div className="w-1 h-1 rounded-full bg-[#30d5c8] flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ArchitectureSection = () => {
  const t = useTr();
  const { ref, inView } = useInView();

  const pillars = [
    {
      id: 'swarmcore', number: '01', icon: GitBranch,
      name: 'SwarmCore',
      subtitle: t('Orchestration Engine', '오케스트레이션 엔진'),
      desc: t(
        'The neural center of every swarm deployment. SwarmCore handles task decomposition, agent assignment, consensus, and failover — enabling thousands of agents to operate as a single coordinated intelligence.',
        '모든 스웜 배포의 신경 중추입니다. SwarmCore는 작업 분해, 에이전트 배정, 합의, 장애 복구를 처리하여 수천 개의 에이전트가 단일 조율 지능으로 운영될 수 있도록 합니다.'
      ),
      features: [
        t('Hierarchical Task Decomposition', '계층적 작업 분해'),
        t('Dynamic Agent Assignment', '동적 에이전트 배정'),
        t('Byzantine Fault Tolerance', '비잔틴 장애 허용'),
        t('Sub-50ms Orchestration', '50ms 미만의 오케스트레이션'),
      ],
    },
    {
      id: 'computemesh', number: '02', icon: Server,
      name: 'ComputeMesh',
      subtitle: t('Decentralized GPU Network', '탈중앙화 GPU 네트워크'),
      desc: t(
        'A peer-to-peer compute marketplace connecting GPU providers globally. Agents spin up, scale, and release compute on-demand at a fraction of centralized costs.',
        '전 세계 GPU 제공자를 연결하는 P2P 컴퓨팅 마켓플레이스입니다. 에이전트는 중앙화 비용의 일부만으로 온디맨드로 컴퓨팅을 시작, 확장, 해제합니다.'
      ),
      features: [
        t('Global GPU Discovery', '글로벌 GPU 검색'),
        t('Automated Resource Bidding', '자동화된 리소스 입찰'),
        t('Cryptographic Compute Proofs', '암호화 컴퓨팅 증명'),
        t('10x Cost Reduction', '10배 비용 절감'),
      ],
    },
    {
      id: 'hivemind', number: '03', icon: MessageSquare,
      name: 'HiveMind',
      subtitle: t('Agent-to-Agent Messaging', '에이전트 간 메시징'),
      desc: t(
        'A purpose-built, low-latency messaging protocol for agent coordination — enabling agents to share memory, stream context, and synchronize state in real time.',
        '에이전트 조율을 위해 특별 설계된 저지연 메시징 프로토콜입니다. 에이전트가 실시간으로 메모리를 공유하고, 컨텍스트를 스트리밍하며, 상태를 동기화할 수 있도록 합니다.'
      ),
      features: [
        t('<10ms Message Delivery', '10ms 미만의 메시지 전달'),
        t('Persistent Memory Streams', '영구 메모리 스트림'),
        t('Multi-Modal Context Sharing', '다중 모달 컨텍스트 공유'),
        t('E2E Encrypted Comms', '종단 간 암호화 통신'),
      ],
    },
    {
      id: 'proof-of-swarm', number: '04', icon: CheckCircle,
      name: 'Proof of Swarm',
      subtitle: t('Verification Protocol', '검증 프로토콜'),
      desc: t(
        'The first cryptographic standard for verifying autonomous agent outputs. Validators stake $SWARM to attest results, creating an immutable audit trail for every agent action.',
        '자율 에이전트 출력물을 검증하는 최초의 암호화 표준입니다. 검증인들이 $SWARM을 스테이킹하여 결과를 증명함으로써, 모든 에이전트 활동에 대한 변경 불가능한 감사 추적을 생성합니다.'
      ),
      features: [
        t('Cryptographic Output Proofs', '암호화 출력 증명'),
        t('Slashing for Dishonest Validators', '부정직한 검증인 슬래싱'),
        t('On-Chain Audit Trail', '온체인 감사 추적'),
        t('GDPR-Compliant Verification', 'GDPR 준수 검증'),
      ],
    },
    {
      id: 'sdk', number: '05', icon: Code2,
      name: 'SwarmBase SDK',
      subtitle: t('Developer Toolkit', '개발자 툴킷'),
      desc: t(
        "Everything developers need to build, deploy, and scale AI agent swarms — in minutes. Pre-built templates, framework integrations, and a one-command swarm launcher.",
        "AI 에이전트 스웜을 구축, 배포, 확장하는 데 필요한 모든 것이 몇 분 안에 준비됩니다. 사전 구축된 에이전트 템플릿, 프레임워크 통합, 원커맨드 스웜 런처를 포함합니다."
      ),
      features: [
        t('Python / TypeScript / Rust SDKs', 'Python / TypeScript / Rust SDK'),
        t('LangChain / AutoGen / CrewAI', 'LangChain / AutoGen / CrewAI 지원'),
        t('50+ Agent Templates', '50개 이상의 에이전트 템플릿'),
        t('One-Command Deploy', '원커맨드 배포'),
      ],
    },
  ];

  return (
    <section id="architecture" className="py-20 sm:py-24 md:py-32 line-grid" style={{ position: 'relative', zIndex: 10, background: 'var(--sb-bg-2)' }} data-testid="architecture-section">
      <div style={{ position: 'absolute', top: '10%', right: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(48,213,200,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 relative">
        <div ref={ref} className={`mb-12 md:mb-16 anim-hidden ${inView ? 'anim-visible' : ''}`}>
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">{t('Core Architecture', '핵심 아키텍처')}</p>
          <h2 className="font-outfit font-black text-white text-3xl sm:text-4xl md:text-5xl tracking-tight">
            {t('Five Pillars of the', 'SwarmBase 프로토콜의')}<br />
            <span className="gradient-text">{t('SwarmBase Protocol', '다섯 가지 핵심 요소')}</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl text-xs sm:text-sm md:text-base leading-relaxed">
            {t('An integrated stack purpose-built for the coordination, compute, and verification demands of autonomous AI swarms.',
               '자율 AI 스웜의 조율, 컴퓨팅, 검증 요구사항을 위해 특별 설계된 통합 스택입니다.')}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {pillars.slice(0, 3).map((p, i) => <PillarCard key={p.id} pillar={p} index={i} />)}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mt-4 sm:mt-5">
          {pillars.slice(3).map((p, i) => <PillarCard key={p.id} pillar={p} index={i + 3} />)}
        </div>
      </div>
    </section>
  );
};

export default ArchitectureSection;
